/* @flow */

import { callLiferayEndpoint } from './index';
import utils from './utils';

import type {
  FindWorklistParams,
  CreateWorklistParams,
  UpdateWorklistParams,
  SimplifiedWorklist,
  SimplifiedTrack,
  Worklist,
  WorklistItem,
  UpdateWorklistItemParams,
  CreateWorklistItemQuotationRequestParams,
} from 'types';

const find = async (params: ?FindWorklistParams): Promise<Worklist[]> => {
  // const [wlResponse, activeWlResponse] = await Promise.all([
  //   callLiferayEndpoint('/protones-portlet.worklists/find-worklists-by-user-simplified'),
  //   callLiferayEndpoint('/protones-portlet.usermanagement/get-active-worklists'),
  // ]);
  // const simplifiedWorklists = wlResponse.map(utils.toSimplifiedWorklist).map(wl => ({
  //   ...wl,
  //   isWorklist1: wl.id === activeWlResponse.activeW1,
  //   isWorklist2: wl.id === activeWlResponse.activeW2,
  // }));
  // return simplifiedWorklists;

  // TODO: fix Protones API to get correct results with find-worklists-by-user.
  // Meanwhile we need to request both the simplified list and each detailed worklist
  const [wlResponse] = await Promise.all([
    callLiferayEndpoint('/protones-portlet.worklists/find-worklists-by-user-simplified'),
    callLiferayEndpoint('/protones-portlet.usermanagement/get-active-worklists'),
  ]);
  const worklistPromises = wlResponse.map(wl => get(wl.worklistId));
  const worklists = await Promise.all(worklistPromises);
  return worklists;
};

const get = async (id: number): Promise<Worklist> => {
  const [wlResponse, activeWlResponse] = await Promise.all([
    callLiferayEndpoint('/protones-portlet.worklists/get', { worklistId: id }),
    callLiferayEndpoint('/protones-portlet.usermanagement/get-active-worklists'),
  ]);
  const worklist = {
    ...wlResponse,
    isWorklist1: wlResponse.worklistId === activeWlResponse.activeW1,
    isWorklist2: wlResponse.worklistId === activeWlResponse.activeW2,
  };
  const parsedWorklist = utils.toWorklist(worklist);
  return parsedWorklist;
};

const create = async (params: CreateWorklistParams): Promise<Worklist> => {
  const usageBox = params.usage
    ? {
        dataInizioUtilizzo: params.usage.startDate,
        dataInizioUtilizzoLocked: params.usage.startDateLocked,
        dataFineUtilizzo: params.usage.endDate,
        dataFineUtilizzoLocked: params.usage.endDateLocked,
        esclusiva: params.usage.exclusive,
        esclusivaLocked: params.usage.exclusiveLocked,
        dataFineEsclusiva: params.usage.exclusiveEndDate,
        dataFineEsclusivaLocked: params.usage.exclusiveEndDateLocked,
        estremiEsclusiva: params.usage.exclusiveNotes,
        estremiEsclusivaLocked: params.usage.exclusiveNotesLocked,
        note: params.usage.notes,
        noteLocked: params.usage.notesLocked,
        produzioneId: params.usage.productionId || 0,
        produzioneIdLocked: params.usage.productionLocked,
        puntata: params.usage.episode,
        puntataLocked: params.usage.episodeLocked,
        canaleId: params.usage.channelId || 0,
        canaleIdLocked: params.usage.channelLocked,
        emittenteId: params.usage.broadcastingId || 0,
        emittenteIdLocked: params.usage.broadcastingLocked,
        piattaformaId: params.usage.platformId || 0,
        piattaformaIdLocked: params.usage.platformLocked,
        tipologiaEmissioneId: params.usage.broadcastTypeId || 0,
        tipologiaEmissioneIdLocked: params.usage.broadcastTypeLocked,
        territorioId: params.usage.territoryId || 0,
        territorioIdLocked: params.usage.territoryLocked,
        tipologiaUtilizzoId: params.usage.usageTypeId || 0,
        tipologiaUtilizzoIdLocked: params.usage.usageTypeLocked,
        tipoId: 220002,
      }
    : undefined;

  const response = await callLiferayEndpoint('/protones-portlet.worklists/add', {
    nome: params.name,
    descrizione: params.description,
    worklistPadreId: params.parentWorklistId || 0,
    usageBox,
  });
  return utils.toWorklist(response);
};

const update = async (params: UpdateWorklistParams): Promise<Worklist> => {
  const updateParams = {
    worklist: {
      worklistId: params.id,
      nome: params.name,
      descrizione: params.description,
      worklistPadreId: params.parentWorklistId || 0,
    },
    usageBox: params.usage
      ? {
          usageBoxId: params.usage.id,
          dataInizioUtilizzo: params.usage.startDate,
          dataInizioUtilizzoLocked: params.usage.startDateLocked,
          dataFineUtilizzo: params.usage.endDate,
          dataFineUtilizzoLocked: params.usage.endDateLocked,
          esclusiva: params.usage.exclusive,
          esclusivaLocked: params.usage.exclusiveLocked,
          dataFineEsclusiva: params.usage.exclusiveEndDate,
          dataFineEsclusivaLocked: params.usage.exclusiveEndDateLocked,
          estremiEsclusiva: params.usage.exclusiveNotes,
          estremiEsclusivaLocked: params.usage.exclusiveNotesLocked,
          note: params.usage.notes,
          noteLocked: params.usage.notesLocked,
          produzioneId: params.usage.productionId || 0,
          produzioneIdLocked: params.usage.productionLocked,
          puntata: params.usage.episode,
          puntataLocked: params.usage.episodeLocked,
          canaleId: params.usage.channelId || 0,
          canaleIdLocked: params.usage.channelLocked,
          emittenteId: params.usage.broadcastingId || 0,
          emittenteIdLocked: params.usage.broadcastingLocked,
          piattaformaId: params.usage.platformId || 0,
          piattaformaIdLocked: params.usage.platformLocked,
          tipologiaEmissioneId: params.usage.broadcastTypeId || 0,
          tipologiaEmissioneIdLocked: params.usage.broadcastTypeLocked,
          territorioId: params.usage.territoryId || 0,
          territorioIdLocked: params.usage.territoryLocked,
          tipologiaUtilizzoId: params.usage.usageTypeId || 0,
          tipologiaUtilizzoIdLocked: params.usage.usageTypeLocked,
        }
      : undefined,
  };
  const [wlResponse, activeWlResponse] = await Promise.all([
    await callLiferayEndpoint('/protones-portlet.worklists/update', updateParams),
    callLiferayEndpoint('/protones-portlet.usermanagement/get-active-worklists'),
  ]);
  const worklist = {
    ...wlResponse,
    isWorklist1: wlResponse.id === activeWlResponse.activeW1,
    isWorklist2: wlResponse.id === activeWlResponse.activeW2,
  };
  return utils.toWorklist(worklist);
};

const _delete = async (id: number): Promise<any> => {
  await callLiferayEndpoint('/protones-portlet.worklists/delete', { worklistId: id });
  return id;
};

const findItems = async (id: number): Promise<WorklistItem[]> => {
  const items = await callLiferayEndpoint(
    '/protones-portlet.worklistitems/find-worklist-items-by-worklist-id',
    { worklistId: id }
  );
  return items.map(utils.toWorklistItem);
};

const createItems = async (
  tracks: SimplifiedTrack[],
  worklist: SimplifiedWorklist,
  usageId: ?number
): Promise<WorklistItem[]> => {
  const response =
    usageId !== null
      ? await callLiferayEndpoint('/protones-portlet.worklistitems/add-with-usage', {
          tracceId: tracks.map(track => track.id),
          targetWorklistId: worklist.id,
          // TODO potrebbe essere necessario creare uno usage apposta con i campi indicati in
          // https://docs.google.com/spreadsheets/d/1uXeZYqbcFY7ZusL9THZa46qroIsiWsIkSPUCRZCPU8I/edit#gid=1041183792
          // in base al contesto (creazione nuova worklist, invio a wl giocattolo)
          usageBoxId: usageId || 0,
        })
      : await callLiferayEndpoint('/protones-portlet.worklistitems/add', {
          tracceId: tracks.map(track => track.id),
          targetWorklistId: worklist.id,
          tipologiaUtilizzoId: 0, // TODO tipologiaUtilizzoId di sessione????
        });
  return response.map(utils.toWorklistItem);
};

const updateItem = async (params: UpdateWorklistItemParams): Promise<WorklistItem> => {
  const updateParams = {
    updateModel: {
      areaId: params.areaId || 0, // "areaId": -1,
      canaleId: params.channelId || 0, // "canaleId": 38,
      dataFineEsclusiva: params.exclusiveEndDate,
      dataFineUtilizzo: params.endDate,
      dataInizioUtilizzo: params.startDate,
      emittenteId: params.broadcastingId || 0, // "emittenteId": 87362,
      esclusiva: params.exclusive,
      estremiEsclusiva: params.exclusiveNotes,
      note: params.notes,
      piattaformaId: params.platformId,
      produzioneId: params.productionId || 0,
      puntata: params.episode,
      territorioId: params.territoryId || 0,
      tipologiaEmissioneId: params.broadcastTypeId || 0, //
      tipologiaUtilizzoId: params.usageTypeId || 0,
      tracciaId: params.trackId,
      worklistId: params.worklistId,
      worklistItemId: params.id,
    },
  };
  const response = await callLiferayEndpoint(
    '/protones-portlet.worklistitems/update',
    updateParams
  );
  return utils.toWorklistItem(response);
};

const deleteItems = async (worklistItems: WorklistItem[]): Promise<number[]> => {
  const response = await callLiferayEndpoint('/protones-portlet.worklistitems/delete', {
    worklistItemIds: worklistItems.map(item => item.id),
  });
  return response.map(item => item.id);
};

const updateWl1 = async (id: number): Promise<Worklist[]> => {
  // const activeWlResponse = await callLiferayEndpoint(
  //   '/protones-portlet.usermanagement/update-active-w1',
  //   { worklistId: id }
  // );
  // const wlResponse = await callLiferayEndpoint(
  //   '/protones-portlet.worklists/find-worklists-by-user-simplified'
  // );
  // const simplifiedWorklists = wlResponse.map(utils.toSimplifiedWorklist).map(wl => ({
  //   ...wl,
  //   isWorklist1: wl.id === activeWlResponse.activeW1,
  //   isWorklist2: wl.id === activeWlResponse.activeW2,
  // }));
  // return simplifiedWorklists;
  await callLiferayEndpoint('/protones-portlet.usermanagement/update-active-w1', {
    worklistId: id,
  });
  const worklists = await find();
  return worklists;
};

const updateWl2 = async (id: number): Promise<Worklist[]> => {
  // const activeWlResponse = await callLiferayEndpoint(
  //   '/protones-portlet.usermanagement/update-active-w2',
  //   {
  //     worklistId: id,
  //   }
  // );
  // const wlResponse = await callLiferayEndpoint(
  //   '/protones-portlet.worklists/find-worklists-by-user-simplified'
  // );
  // const simplifiedWorklists = wlResponse.map(utils.toSimplifiedWorklist).map(wl => ({
  //   ...wl,
  //   isWorklist1: wl.id === activeWlResponse.activeW1,
  //   isWorklist2: wl.id === activeWlResponse.activeW2,
  // }));
  // return simplifiedWorklists;
  await callLiferayEndpoint('/protones-portlet.usermanagement/update-active-w2', {
    worklistId: id,
  });
  const worklists = await find();
  return worklists;
};

const createWorklistItemQuotationRequest = async (
  params: CreateWorklistItemQuotationRequestParams
): Promise<WorklistItem> => {
  const response = await callLiferayEndpoint(
    '/protones-portlet.worklistitems/request-worklist-item-component-quotations',
    {
      worklistItemId: params.worklistItemId,
      tipoDirittoIds: params.rightIds,
    }
  );
  return utils.toWorklistItem(response);
};

export default {
  find,
  get,
  create,
  update,
  delete: _delete,
  wl1: {
    update: updateWl1,
  },
  wl2: { update: updateWl2 },
  items: {
    find: findItems,
    create: createItems,
    update: updateItem,
    delete: deleteItems,
  },
  askQuotation: createWorklistItemQuotationRequest,
};
